import {
  Box,
  debounce,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { CopyToClipboardButton, DynamicIcon, Tooltip } from 'src/components';
import { AutocompleteSearchBar } from 'src/components/SearchBar/autocomplete-search-bar';
import { SidePanelCaption } from 'src/components/SidePanelCaption';
import { SideTabStyledWrapper } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteAddress } from 'src/components/SiteComponents/site-address';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address, Conversation, useAddress } from 'src/graphql';
import { Modals, useModalMethods } from 'src/store/modals';
import { useInViewTrigger, useNotifications, useOpenSite } from 'src/utils/hooks';
import { ConversationSite } from './conversation-site';
import { useConversationAddress } from './use-conversation-address';
import { useConversationAssociatedSites } from './use-conversation-associated-sites';

interface ConversationSitesProps {
  conversation: Conversation;
  /** Lets the hosting card show the "+N more" chip in its header. */
  onAvailableSitesCountChange?: (count: number) => void;
}

const AddressItemSkeleton = () => (
  <Stack
    ml={1}
    overflow="hidden"
    flexGrow={1}
    direction="row"
    spacing={1}
    alignItems="center"
    padding="0.8rem"
  >
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton variant="circular" width={20} height={20} />
    <Skeleton width="70%" />
  </Stack>
);

export const ConversationSites = ({
  conversation,
  onAvailableSitesCountChange,
}: ConversationSitesProps) => {
  const theme = useTheme();
  const [input, setInput] = useState('');

  const { showSuccessAlert } = useNotifications();

  const { showModal: showConversationToAddressModal } = useModalMethods(
    Modals.UpdateConversationToAddress,
  );

  const conversationAddress = useConversationAddress(conversation);

  const {
    addresses: clientAddresses,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    updateAssociatedSitesCache,
    filters,
  } = useConversationAssociatedSites(conversation, input);

  const associatedAddressesCount = useMemo(() => {
    const uniqueAddressIds = new Set(
      [conversationAddress, ...clientAddresses]
        .filter((site): site is Address => !!site)
        .map((site) => site.addressId),
    );
    return uniqueAddressIds.size;
  }, [clientAddresses, conversationAddress]);

  useEffect(() => {
    onAvailableSitesCountChange?.(conversationAddress ? associatedAddressesCount : 0);
  }, [associatedAddressesCount, conversationAddress, onAvailableSitesCountChange]);

  const inViewElementRef = useInViewTrigger({
    onEnter: loadMore,
    enabled: !loading && !!hasMore && !isLoadingMore,
  });

  const { createConversationToAddress, createClientToAddress } = useAddress(
    conversationAddress?.addressId,
  );

  const openSite = useOpenSite();

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setInput(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const requesterEntity = conversation.requester
    ?.participantEntity as ConversationParticipantEntity;

  const onSelectSite = useCallback(
    async (newValue: Address | null) => {
      if (!newValue) {
        return;
      }

      if (
        !clientAddresses.find((it) => it.addressId === newValue.addressId) &&
        requesterEntity === ConversationParticipantEntity.Client &&
        conversation.requester.participantIdentity !== 'unknown'
      ) {
        await createClientToAddress({
          client: {
            ...(conversation.requester?.client ?? {}),
            id:
              conversation.requester?.client?.clientId ??
              conversation.requester?.participantIdentity,
            clientId:
              conversation.requester?.client?.clientId ??
              conversation.requester?.participantIdentity,
          },
          address: newValue,
          partnerId: conversation.partnerId,
          filters,
        });
        updateAssociatedSitesCache(newValue);
      }

      const response = await createConversationToAddress(
        conversation.conversationId,
        newValue,
        conversation.partnerId,
      );

      if (!response) {
        return;
      }

      setInput('');

      showSuccessAlert('Site was added to the conversation');
    },
    [
      clientAddresses,
      conversation.conversationId,
      conversation.partnerId,
      conversation.requester?.client,
      conversation.requester.participantIdentity,
      createClientToAddress,
      createConversationToAddress,
      filters,
      requesterEntity,
      showSuccessAlert,
      updateAssociatedSitesCache,
    ],
  );

  const onAddButtonClick = useCallback(() => {
    showConversationToAddressModal({
      conversation,
      onSelect: (_, { clientAddress = null, siteToAdd = null }) => {
        const newValue = clientAddress || siteToAdd;
        if (!newValue) {
          return;
        }
        void onSelectSite(newValue);
      },
    });
  }, [conversation, onSelectSite, showConversationToAddressModal]);

  if (conversationAddress) {
    // remount per site so one site's in-flight save state never shows on the next
    return (
      <ConversationSite key={conversationAddress.addressId} conversation={conversation} />
    );
  }

  if (clientAddresses.length || input || loading) {
    return (
      <SideTabStyledWrapper>
        <SidePanelCaption paddingBottom={1}>Select Associated Site</SidePanelCaption>
        <AutocompleteSearchBar
          addButtonText="+ Add Site"
          addButtonTestId="conversation-sites-add-site"
          onAddButtonClick={onAddButtonClick}
          handleOnFilterResultsChange={handleOnChange}
        />
        <Box display="grid">
          {!!loading && !clientAddresses.length && <AddressItemSkeleton />}
          {clientAddresses.map((clientAddress) => (
            <Stack
              key={clientAddress.addressId}
              direction="row"
              spacing={1}
              alignItems="center"
              overflow="hidden"
              width="100%"
            >
              <Tooltip title="Associate address" hideWhenRefIsHidden>
                <IconButton
                  onClick={async () => {
                    await createConversationToAddress(
                      conversation.conversationId,
                      clientAddress,
                      conversation.partnerId,
                    );
                    setInput('');
                  }}
                >
                  <DynamicIcon
                    name="add_circle"
                    filled
                    color={theme.palette.custom.OV.sys.secondary}
                  />
                </IconButton>
              </Tooltip>
              <Stack direction="row" spacing={1} alignItems="center" flexGrow={1} minWidth={0}>
                <SiteAddress
                  address={clientAddress}
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    openSite(event, clientAddress)
                  }
                  tooltipText="Click to open site"
                />
                <Box flexGrow={1} />
                <CopyToClipboardButton content={clientAddress.fullAddress!} />
              </Stack>
            </Stack>
          ))}
          {!!isLoadingMore && <AddressItemSkeleton />}
          <Box ref={inViewElementRef} sx={{ height: 2 }} />
          {!loading && !clientAddresses?.length && (
            <Stack padding="0.5rem">
              <Typography alignSelf="center" variant="body2">
                No results
              </Typography>
            </Stack>
          )}
        </Box>
      </SideTabStyledWrapper>
    );
  }

  return (
    <SideTabStyledWrapper>
      <SidePanelCaption paddingBottom={1}>Associated Site</SidePanelCaption>
      <AutocompleteSearchBar
        addButtonText="+ Add Site"
        addButtonTestId="conversation-sites-add-site"
        onAddButtonClick={onAddButtonClick}
        handleOnFilterResultsChange={handleOnChange}
        hideSearchBar
      />
    </SideTabStyledWrapper>
  );
};
